
import React, { useState, useEffect } from 'react';
import { X, Minimize2, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface LoadingOverlayProps {
  visible: boolean;
  onCancel?: () => void;
  onMinimize?: () => void;
  statusText?: string;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ visible, onCancel, onMinimize, statusText }) => {
  const { t } = useTranslation();
  const [statusIdx, setStatusIdx] = useState(0);
  const [showControls, setShowControls] = useState(false);
  
  const statuses = [
    "Henter terrengdata...",
    "Sjekker 150-metersregelen...",
    "Analyserer satellittbilder...",
    "Renser ut uegnede plasser...",
    "Vurderer eiendomsgrenser...",
    "Fjerner lavkvalitets-resultater...",
    "Optimaliserer funn..."
  ];

  useEffect(() => {
    if (!visible) {
      setShowControls(false);
      return;
    }
    
    const interval = setInterval(() => {
      setStatusIdx(prev => (prev + 1) % statuses.length);
    }, 2000);
    
    // Vis knapper etter 2.5 sekunder
    const timer = setTimeout(() => {
      setShowControls(true);
    }, 2500);
    
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[10010] bg-[var(--bg-overlay)] backdrop-blur-2xl flex flex-col items-center justify-center p-6 animate-in fade-in duration-500 text-center pointer-events-none">
      <div className="relative mb-12">
        <div className="absolute inset-0 bg-brand/10 blur-[120px] rounded-full animate-pulse scale-150" />
        <div className="relative bg-white rounded-[4rem] shadow-[0_50px_100px_rgba(0,0,0,0.8)] animate-bounce flex items-center justify-center w-64 h-64 md:w-80 md:h-80 overflow-hidden border-4 border-[var(--border-secondary)]">
           <img 
            src="/Fricamplogo.png" 
            alt="Fricamp Logo" 
            className="w-full h-full object-cover"
          />
        </div>
      </div>
      
      <div className="space-y-4 max-w-sm">
        <h2 className="text-[var(--text-primary)] font-black text-3xl tracking-tighter drop-shadow-lg">{t("auto.loadingoverlay.skanner_renser", "Skanner & Renser")}</h2>
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand animate-ping" />
            <p className="text-brand font-black text-[12px] uppercase tracking-[0.5em] animate-pulse">
              {statusText || statuses[statusIdx]}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-12 w-72 h-1.5 bg-[var(--bg-surface-alt)] rounded-full overflow-hidden border border-[var(--border-primary)] shadow-inner relative">
        <div className="h-full bg-brand animate-[loading-bar_1.5s_infinite]" />
      </div>

      {showControls && (
        <div className="mt-16 flex flex-col md:flex-row gap-4 animate-in slide-in-from-bottom-4 duration-500 pointer-events-auto">
          <button 
            onClick={(e) => { e.preventDefault(); onMinimize?.(); }}
            className="flex items-center gap-3 px-8 py-4 bg-emerald-600/10 hover:bg-emerald-600/20 border border-emerald-500/20 rounded-2xl text-2xs font-black text-emerald-400 uppercase tracking-widest transition-all shadow-xl active:scale-95 pointer-events-auto group"
          >
            <Minimize2 size={16} /> {t("auto.loadingoverlay.fortsett_i_bakgrunnen", "Fortsett i bakgrunnen")} <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
          
          <button 
            onClick={(e) => { e.preventDefault(); onCancel?.(); }}
            className="flex items-center gap-3 px-8 py-4 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest transition-all active:scale-95 pointer-events-auto"
          >
            <X size={16} /> {t("auto.loadingoverlay.avbryt", "Avbryt")}
          </button>
        </div>
      )}
      
      <style>{`
        @keyframes loading-bar {
          0% { width: 0%; transform: translateX(-100%); }
          50% { width: 50%; transform: translateX(50%); }
          100% { width: 0%; transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
};

export default LoadingOverlay;
